/**
 * White label — single point that resolves and applies the effective brand to the DOM.
 *
 * Source of truth is the API (`/white-label/effective` and
 * `/public/white-label/resolve`); company configuration is never read for branding.
 * Process portals are an overlay: `applyPortalShellBrand` paints portal logo/color
 * over the company brand without mutating the stored white-label snapshot.
 *
 * The API only answers with a brand it actually resolved — a 204 means there is
 * none — so the AUTOMY defaults are owned here, and `null` is the value that
 * asks for them.
 */
import { ConfigProvider } from 'antd';

import defaultFavicon from '@/assets/icons/favicon.svg';
import defaultLogo from '@/assets/logo.svg';
import env from '@/config/env';
import HttpService from '@/services/http';
import { getSessionToken } from '@/utils/infrastructure/auth';
import {
  AUTOMY_ACCENT,
  applyAccentCssVariables,
  getColorPalette,
} from '@/utils/infrastructure/utilities/colors';

const AUTHENTICATED_ENDPOINT = '/white-label/effective';
const PUBLIC_ENDPOINT = '/public/white-label/resolve';

const FONT_STYLE_ID = 'automy-white-label-font';
const FONT_PRELOAD_ID = 'automy-white-label-font-preload';

export const BRAND_FONT_FAMILY = 'AutomyBrandFont';

/** API enums are 1-based. `Default` only exists server-side, for email templates. */
export const WHITE_LABEL_SOURCE = Object.freeze({
  Own: 1,
  Inherited: 2,
  Default: 3,
});

/**
 * AUTOMY fallback. The API never sends a default snapshot — it answers 204 when
 * a company has no applicable brand — so the product defaults live here, on
 * bundled assets that no network failure can take away.
 */
const DEFAULT_SNAPSHOT = Object.freeze({
  targetCompanyId: null,
  sourceCompanyId: null,
  source: WHITE_LABEL_SOURCE.Default,
  brandName: 'AUTOMY',
  mainColor: AUTOMY_ACCENT,
  logoUrl: defaultLogo,
  iconLogoUrl: defaultFavicon,
  faviconUrl: defaultFavicon,
  fontUrl: null,
  loginSplashUrl: null,
  supportEmail: null,
  customDomain: null,
  version: 0,
  isDefault: true,
});

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

let currentSnapshot = { ...DEFAULT_SNAPSHOT };
let pendingAuthenticated = null;
/** Process portal owns the painted shell while set; company WL stays in `currentSnapshot`. */
let portalShellBrand = null;
const listeners = new Set();

const safeUrl = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
};

const safeText = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export function getDefaultWhiteLabel() {
  return { ...DEFAULT_SNAPSHOT };
}

export function getCurrentWhiteLabel() {
  return { ...currentSnapshot };
}

/** Accent/logo painted for an active process portal, or `null` when the shell is company WL. */
export function getPortalShellBrand() {
  return portalShellBrand ? { ...portalShellBrand } : null;
}

export function isPortalShellActive() {
  return !!portalShellBrand;
}

/**
 * A resolved brand is always `Own` or `Inherited` — the API answers 204 instead
 * of sending a default one. `source` is only inferred when the payload omits it,
 * and never lands on `Default`.
 */
const resolveSource = (raw, targetCompanyId, sourceCompanyId) => {
  if (Number.isInteger(raw.source)) return raw.source;
  if (!sourceCompanyId) return WHITE_LABEL_SOURCE.Own;
  return String(sourceCompanyId).toLowerCase() === String(targetCompanyId).toLowerCase()
    ? WHITE_LABEL_SOURCE.Own
    : WHITE_LABEL_SOURCE.Inherited;
};

/**
 * `null` — a 204, or nothing resolvable — means AUTOMY. Anything else is a real
 * brand whose missing or malformed fields fall back to their AUTOMY default: a
 * tenant that only brands part of the platform keeps the standard mark
 * everywhere else, and a partial response can never break the shell.
 */
export function normalizeWhiteLabel(raw) {
  if (!raw || typeof raw !== 'object') return getDefaultWhiteLabel();
  // Our own default snapshot, round-tripping back through `applyWhiteLabel`.
  if (raw.isDefault === true || raw.source === WHITE_LABEL_SOURCE.Default) {
    return getDefaultWhiteLabel();
  }

  const targetCompanyId = raw.targetCompanyId || null;
  const sourceCompanyId = raw.sourceCompanyId || null;
  const mainColor = HEX_COLOR_PATTERN.test(String(raw.mainColor || '').trim())
    ? String(raw.mainColor).trim()
    : DEFAULT_SNAPSHOT.mainColor;

  return {
    targetCompanyId,
    sourceCompanyId,
    source: resolveSource(raw, targetCompanyId, sourceCompanyId),
    brandName: safeText(raw.brandName) || DEFAULT_SNAPSHOT.brandName,
    mainColor,
    logoUrl: safeUrl(raw.logoUrl) || DEFAULT_SNAPSHOT.logoUrl,
    iconLogoUrl: safeUrl(raw.iconLogoUrl) || DEFAULT_SNAPSHOT.iconLogoUrl,
    faviconUrl: safeUrl(raw.faviconUrl) || DEFAULT_SNAPSHOT.faviconUrl,
    fontUrl: safeUrl(raw.fontUrl),
    loginSplashUrl: safeUrl(raw.loginSplashUrl),
    supportEmail: safeText(raw.supportEmail),
    customDomain: safeText(raw.customDomain),
    version: Number.isFinite(raw.version) ? raw.version : 0,
    isDefault: false,
  };
}

export function subscribeWhiteLabel(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener(getCurrentWhiteLabel());
    } catch (e) {}
  });
};

const applyCssVariables = (snapshot) => {
  const root = document.documentElement;
  if (!root) return;

  const { textColor } = getColorPalette(snapshot.mainColor);
  const accent = snapshot.mainColor;
  const contrast = textColor || '#fff';

  // Platform brand on `:root` (`--wl-*` + accent tokens). Form `mainColor` is
  // scoped under `.automy-form-style-scope` and never overwrites the shell.
  root.style.setProperty('--wl-main-color', accent);
  root.style.setProperty('--wl-main-color-contrast', contrast);
  applyAccentCssVariables(root, accent, contrast);
  root.style.setProperty('--wl-logo-url', `url("${snapshot.logoUrl}")`);
  root.style.setProperty('--wl-icon-logo-url', `url("${snapshot.iconLogoUrl}")`);
  root.style.setProperty(
    '--wl-login-splash-url',
    snapshot.loginSplashUrl ? `url("${snapshot.loginSplashUrl}")` : 'none',
  );
  root.style.setProperty('--wl-font-family', snapshot.fontUrl ? `'${BRAND_FONT_FAMILY}'` : '');
};

const applyFavicon = (faviconUrl) => {
  if (!faviconUrl) return;

  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'icon');
    document.head.appendChild(link);
  }

  if (link.getAttribute('href') !== faviconUrl) link.setAttribute('href', faviconUrl);
};

/**
 * A remote asset that 404s must not leave a hole in the shell. Loading it out of
 * band lets the snapshot swap in the bundled AUTOMY asset, so every consumer —
 * CSS variables, favicon and each component reading the hook — recovers at once.
 */
const probeAsset = (key) => {
  const url = currentSnapshot[key];
  const fallback = DEFAULT_SNAPSHOT[key];
  if (!url || url === fallback) return;

  const probe = new Image();
  probe.onerror = () => {
    // A newer snapshot was painted while this one was loading.
    if (currentSnapshot[key] !== url) return;

    currentSnapshot = { ...currentSnapshot, [key]: fallback };
    applyCssVariables(currentSnapshot);
    if (key === 'faviconUrl') applyFavicon(fallback);
    notify();
  };
  probe.src = url;
};

const applyFont = (fontUrl) => {
  document.getElementById(FONT_STYLE_ID)?.remove();
  document.getElementById(FONT_PRELOAD_ID)?.remove();
  if (!fontUrl) return;

  const preload = document.createElement('link');
  preload.id = FONT_PRELOAD_ID;
  preload.setAttribute('rel', 'preload');
  preload.setAttribute('as', 'font');
  preload.setAttribute('type', 'font/woff2');
  preload.setAttribute('crossorigin', 'anonymous');
  preload.setAttribute('href', fontUrl);
  document.head.appendChild(preload);

  const style = document.createElement('style');
  style.id = FONT_STYLE_ID;
  style.setAttribute('type', 'text/css');
  style.appendChild(
    document.createTextNode(`
      @font-face {
        font-family: '${BRAND_FONT_FAMILY}';
        src: url("${fontUrl}") format('woff2');
        font-display: swap;
      }
    `),
  );
  document.head.appendChild(style);
};

/**
 * Paint a snapshot. This is the only function in the app allowed to write brand
 * assets to the DOM (alongside `applyPortalShellBrand` for process portals).
 *
 * @param {object|null} snapshot An `EffectiveWhiteLabel`, or `null` for AUTOMY.
 * @param {object} [options]
 * @param {boolean} [options.updateTitle=true] Write `brandName` to `document.title`.
 *   Turn it off where a `Helmet` owns the title.
 * @param {boolean} [options.updateTheme=true] Push `mainColor` into Ant Design.
 *   Turn it off where a narrower theme (per-process public style) must win.
 * @param {boolean} [options.updateFavicon=true]
 * @param {boolean} [options.forcePaint=false] Paint even while a portal owns the shell
 *   (used when restoring company WL after leaving a portal).
 */
export function applyWhiteLabel(snapshot, options = {}) {
  const {
    updateTitle = true,
    updateTheme = true,
    updateFavicon = true,
    forcePaint = false,
  } = options;
  const next = normalizeWhiteLabel(snapshot);

  currentSnapshot = next;

  // Process portal mark wins over company white-label for the painted shell.
  // Keep the company snapshot in memory so leaving the portal can restore it.
  if (portalShellBrand && !forcePaint) {
    notify();
    return next;
  }

  try {
    applyCssVariables(next);
    if (updateFavicon) applyFavicon(next.faviconUrl);
    applyFont(next.fontUrl);

    if (updateTheme) {
      ConfigProvider.config({ theme: { primaryColor: next.mainColor } });
    }
    if (updateTitle) document.title = next.brandName;

    ['logoUrl', 'iconLogoUrl', 'faviconUrl', 'loginSplashUrl'].forEach(probeAsset);
  } catch (e) {}

  notify();
  return next;
}

/**
 * Paint process-portal branding onto the shell. Portal config (logo, mainColor,
 * font, splash) must win over company white-label for the whole portal session.
 * Does not mutate the stored company white-label snapshot.
 */
export function applyPortalShellBrand(portal, options = {}) {
  const { updateTheme = true, updateFavicon = false } = options;
  if (!portal) return clearPortalShellBrand(options);

  const mainColor = HEX_COLOR_PATTERN.test(String(portal.mainColor || '').trim())
    ? String(portal.mainColor).trim()
    : AUTOMY_ACCENT;

  const shell = {
    mainColor,
    logoUrl: safeUrl(portal.logoUrl) || DEFAULT_SNAPSHOT.logoUrl,
    iconLogoUrl: safeUrl(portal.iconLogoUrl) || DEFAULT_SNAPSHOT.iconLogoUrl,
    faviconUrl: safeUrl(portal.faviconUrl) || DEFAULT_SNAPSHOT.faviconUrl,
    fontUrl: safeUrl(portal.fontUrl),
    loginSplashUrl: safeUrl(portal.loginSplashUrl),
    brandName: safeText(portal.name) || DEFAULT_SNAPSHOT.brandName,
  };

  portalShellBrand = shell;

  try {
    applyCssVariables(shell);
    if (updateFavicon) applyFavicon(shell.faviconUrl);
    applyFont(shell.fontUrl);
    if (updateTheme) {
      ConfigProvider.config({ theme: { primaryColor: shell.mainColor } });
    }
  } catch (e) {}

  return { ...shell };
}

/** Drop portal shell paint and restore the stored company white-label. */
export function clearPortalShellBrand(options = {}) {
  if (!portalShellBrand) return getCurrentWhiteLabel();
  portalShellBrand = null;
  return applyWhiteLabel(currentSnapshot, {
    updateTitle: false,
    forcePaint: true,
    ...options,
  });
}

export function resetWhiteLabelToDefault(options = {}) {
  pendingAuthenticated = null;
  return applyWhiteLabel(null, options);
}

/** An empty 204 body arrives here as `''`, and a resolved brand as an object. */
const asSnapshot = (body) => (body && typeof body === 'object' ? body : null);

/**
 * Effective brand for the signed-in session, or `null` when there is none to
 * apply. Concurrent callers share one in-flight request.
 *
 * A 204 (no brand of its own and nothing inherited, or an opted-out one) reaches
 * us as an empty body, which `asSnapshot` reads as "paint AUTOMY". A transport
 * failure lands in the same place: the shell keeps the neutral brand rather than
 * a stale tenant's.
 */
export async function resolveAuthenticatedWhiteLabel() {
  const token = getSessionToken();
  if (!token?.accessToken) return null;
  if (pendingAuthenticated) return pendingAuthenticated;

  pendingAuthenticated = HttpService.getInstance()
    .request(AUTHENTICATED_ENDPOINT, { suppressErrorNotification: true })
    .then(asSnapshot)
    .catch(() => null)
    .finally(() => {
      pendingAuthenticated = null;
    });

  return pendingAuthenticated;
}

const readContextFromLocation = () => {
  try {
    return new URLSearchParams(window.location.search).get('context');
  } catch (e) {
    return null;
  }
};

/**
 * Effective brand for anonymous surfaces (login, password recovery, public and
 * external forms), or `null` when there is none to apply. The API resolves the
 * tenant from the request hostname, or from a signed `context` token when the
 * host is AUTOMY's — the company id is never sent by the client, and stored
 * sessions are not an authority here.
 *
 * Uses native `fetch` on purpose: the shared request interceptor aborts calls
 * made without a session token outside `/external/` style fragments.
 */
export async function resolvePublicWhiteLabel({ context } = {}) {
  const resolvedContext = context === undefined ? readContextFromLocation() : context;
  const query = resolvedContext ? `?context=${encodeURIComponent(resolvedContext)}` : '';

  try {
    const response = await fetch(`${env.API_URL}${PUBLIC_ENDPOINT}${query}`, {
      method: 'GET',
      headers: { clientId: env.CLIENT_ID },
      credentials: 'omit',
    });
    // 204: an AUTOMY host without context, or a hostname that matches no brand.
    if (response.status === 204 || !response.ok) return null;
    return asSnapshot(await response.json());
  } catch (e) {
    return null;
  }
}

export async function bootstrapAuthenticatedWhiteLabel(options) {
  return applyWhiteLabel(await resolveAuthenticatedWhiteLabel(), options);
}

export async function bootstrapPublicWhiteLabel({ context, ...options } = {}) {
  return applyWhiteLabel(await resolvePublicWhiteLabel({ context }), options);
}

// Seed AUTOMY accent tokens as soon as the module loads so components that read
// `--form-accent-color` never flash an empty cascade before the first resolve.
if (typeof document !== 'undefined') {
  applyCssVariables(DEFAULT_SNAPSHOT);
}
